import { ChevronDown, Lock } from "@tamagui/lucide-icons";
import React from "react";
import { Keyboard } from "react-native";
import {
  GetProps,
  Handle,
  Overlay,
  SizableText,
  type TamaguiElement,
  Frame as TamaguiFrame,
  Select as TamaguiSelect,
  Text,
  createSheet,
  styled,
  withStaticProperties,
} from "tamagui";

import FormField from "@/components/forms/FormField";
import { useFormFieldAria } from "@/components/forms/useFormFieldAria";
import { useFormFieldIds } from "@/components/forms/useFormFieldIds";

export const Sheet = createSheet({
  Frame: TamaguiFrame,
  Handle,
  Overlay,
});

const StyledSelectItem = styled(TamaguiSelect.Item, {
  unstyled: true, // Styles here are not merging cleanly with default styles
  minHeight: "$550",
  paddingVertical: 0,
  // Align item text with the trigger's value text (both inset by $150): the
  // Group insets items by $50, so items add the remaining $100 — which doubles
  // as the hover/focus highlight's internal padding so text isn't cramped.
  paddingHorizontal: "$100",
  justifyContent: "center",
  backgroundColor: "$transparent",
  role: "option",
  cursor: "pointer",
  // The highlight sits ~$75 inside the Viewport's $150 radius, so it uses the
  // laddered-down $75 radius to stay concentric with the popover's corners.
  focusVisibleStyle: {
    outlineWidth: "$strokeWidth",
    outlineStyle: "solid",
    outlineColor: "$outlineColor",
    backgroundColor: "$navSelected",
    borderRadius: "$75",
  },
  hoverStyle: {
    backgroundColor: "$navSelected",
    borderRadius: "$75",
  },
});

const PlaceholderText = styled(Text, {
  name: "SelectItemText",
  color: "$placeholderColor",
  margin: 0,
  fontFamily: "$sans-serif",
});

type SelectItemValueT = object | string | number;
type SelectItemT = {
  label: string;
  value: SelectItemValueT;
};

type SelectProps = {
  ref?: React.Ref<TamaguiElement>;
  items: Array<SelectItemT>;
  placeholder?: string;
  label?: string;
  value?: SelectItemValueT;
  testID?: string;
  disabled?: boolean;
  errorText?: string;
  onValueChange?:
    | ((value: object) => void)
    | ((value: number) => void)
    | ((value: string) => void)
    | ((value: undefined) => void);
} & Omit<GetProps<typeof TamaguiSelect>, "value" | "onValueChange">;

const SelectFrame = ({
  name,
  items,
  label,
  onValueChange,
  placeholder,
  value,
  id,
  testID,
  disabled,
  errorText,
  ref,
  ...restProps
}: SelectProps) => {
  const { fieldId, labelId, errorId } = useFormFieldIds(id);
  const formFieldAria = useFormFieldAria({
    hasLabel: Boolean(label),
    labelId,
    errorId,
    errorText,
  });
  const selectedItemLabel = items.find((item) => item.value === value)?.label;

  const memoizedItems = React.useMemo(
    () =>
      items.map((item, i) => (
        <StyledSelectItem
          index={i}
          key={item.label}
          testID={testID ? `${testID}:${item.label}` : undefined}
          // Purposely overriding to allow flexibility for value
          // @ts-expect-error Type '{ label: string; value: string | number | object; }' is not assignable to type 'string' ts(2322)
          value={item.value}
        >
          <TamaguiSelect.ItemText fontSize="$md" fontFamily="$sans-serif">
            {item.label}
          </TamaguiSelect.ItemText>
        </StyledSelectItem>
      )),
    [items, testID],
  );

  const borderColor = disabled
    ? "$strokeSubdued"
    : errorText
      ? "$red"
      : "$strokeNeutral";

  return (
    <FormField
      label={label}
      fieldId={fieldId}
      errorText={errorText}
      rootProps={restProps}
      labelAccessory={
        disabled ? (
          <Lock
            strokeWidth={4}
            size="$150"
            color="$color"
            role="presentation"
          />
        ) : undefined
      }
    >
      <TamaguiSelect
        id={fieldId}
        name={name}
        disablePreventBodyScroll
        // Purposely overriding to allow flexibility for value
        // @ts-expect-error Type 'string | number | object | undefined' is not assignable to type 'string | undefined'. Type 'number' is not assignable to type 'string'.ts(2322)
        value={value}
        // @ts-expect-error Type '((value: SelectItemT) => void) | ((value: string) => void) | undefined' is not assignable to type '((value: string) => void) | undefined'. ts(2322)
        onValueChange={onValueChange}
        onOpenChange={Keyboard?.dismiss}
        // below is to block dropdown from opening if select is disabled
        {...(disabled ? { open: false } : undefined)}
      >
        <TamaguiSelect.Trigger
          ref={ref}
          testID={testID}
          id={fieldId}
          iconAfter={ChevronDown}
          height="$600"
          disabled={disabled}
          disabledStyle={{ opacity: 1 }}
          paddingVertical={0}
          paddingHorizontal="$150"
          backgroundColor={disabled ? "$fillNeutral" : "$fillInput"}
          borderWidth="$strokeWidth"
          borderColor={borderColor}
          borderRadius="$150"
          outlineOffset="$25"
          {...(disabled
            ? { cursor: "not-allowed", pointerEvents: "none" }
            : {})}
          hoverStyle={{
            borderColor,
            backgroundColor: disabled ? "$fillNeutral" : "$fillInput",
          }}
          {...formFieldAria}
          {...(label ? { "aria-labelledby": labelId } : {})}
        >
          <TamaguiSelect.Value
            fontSize="$md"
            color="$textPrimary"
            fontFamily="$sans-serif"
          >
            {selectedItemLabel ? (
              <SizableText>{selectedItemLabel} </SizableText>
            ) : (
              // NOTE: TamaguiSelect.Value uses unwrapSelectItem() which will unwrap one layer of PlaceholderText
              <PlaceholderText>
                <PlaceholderText>{placeholder}</PlaceholderText>
              </PlaceholderText>
            )}
          </TamaguiSelect.Value>
        </TamaguiSelect.Trigger>

        <TamaguiSelect.Adapt when="maxLg">
          <Sheet
            modal
            unmountChildrenWhenHidden
            // Drag stays enabled (v2 only wires the scroll/pan arbiter when
            // the pan responder is active, so `disableDrag` would break list
            // scrolling). That makes the sheet drag-dismissable.
            dismissOnSnapToBottom
            snapPointsMode="fit"
          >
            <Sheet.Frame backgroundColor="$fillModal">
              <Sheet.ScrollView
                paddingHorizontal="$150"
                marginTop="$200"
                maxHeight="80vh"
              >
                <TamaguiSelect.Adapt.Contents />
              </Sheet.ScrollView>
            </Sheet.Frame>
            <Sheet.Overlay
              backgroundColor="$fillOverlay"
              backdropFilter="blur(8px)"
            />
          </Sheet>
        </TamaguiSelect.Adapt>
        <TamaguiSelect.Content zIndex={999999} margin="$200">
          <TamaguiSelect.ScrollUpButton />
          <TamaguiSelect.Viewport
            elevation="$100"
            borderWidth="$strokeWidth"
            borderColor="$strokeNeutral"
            backgroundColor="$fillModal"
            overflow="hidden"
            borderRadius="$150"
          >
            <TamaguiSelect.Group
              overflowY="auto"
              style={{ scrollbarWidth: "none" }}
              gap="$50"
              padding="$50"
              $lg={{
                maxHeight: "$2400",
              }}
            >
              {memoizedItems}
            </TamaguiSelect.Group>
          </TamaguiSelect.Viewport>

          <TamaguiSelect.ScrollDownButton />
        </TamaguiSelect.Content>
      </TamaguiSelect>
    </FormField>
  );
};

SelectFrame.displayName = "Select";

export default withStaticProperties(SelectFrame, {});
